import React, { createContext, useContext, useState, useEffect } from 'react';

// ---- Locale dictionaries (split per namespace under src/locales/{en,es}/) ----
import enFlat from '../locales/en/flat.json';
import enCommon from '../locales/en/common.json';
import enVotd from '../locales/en/votd.json';
import enPlans from '../locales/en/plans.json';
import enHeader from '../locales/en/header.json';
import enTools from '../locales/en/tools.json';
import enReader from '../locales/en/reader.json';
import enCommunity from '../locales/en/community.json';
import enGames from '../locales/en/games.json';
import enSermon from '../locales/en/sermon.json';
import enMessages from '../locales/en/messages.json';
import enPrayer from '../locales/en/prayer.json';
import enSettings from '../locales/en/settings.json';

import esFlat from '../locales/es/flat.json';
import esCommon from '../locales/es/common.json';
import esVotd from '../locales/es/votd.json';
import esPlans from '../locales/es/plans.json';
import esHeader from '../locales/es/header.json';
import esTools from '../locales/es/tools.json';
import esReader from '../locales/es/reader.json';
import esCommunity from '../locales/es/community.json';
import esGames from '../locales/es/games.json';
import esSermon from '../locales/es/sermon.json';
import esMessages from '../locales/es/messages.json';
import esPrayer from '../locales/es/prayer.json';
import esSettings from '../locales/es/settings.json';

/**
 * i18n dictionary assembled from per-namespace JSON files.
 *
 * Shapes:
 *  1. Flat keys (legacy) — spread from flat.json, kept for `t('save')` style calls.
 *  2. Namespaced objects (votd, plans, header, tools, reader, community, games, sermon, messages)
 *     — access with dot-notation: `t('community.title')`.
 *
 * `t()` supports `{token}` interpolation and falls back current-lang → English → the key.
 *
 * To add strings: edit the matching src/locales/{en,es}/<namespace>.json (keep EN/ES key parity).
 * To add a namespace: drop a new <namespace>.json in both folders and import + register it below.
 */
const translations = {
  en: {
    ...enFlat,
    common: enCommon,
    votd: enVotd,
    plans: enPlans,
    header: enHeader,
    tools: enTools,
    reader: enReader,
    community: enCommunity,
    games: enGames,
    sermon: enSermon,
    messages: enMessages,
    prayer: enPrayer,
    settings: enSettings,
  },
  es: {
    ...esFlat,
    common: esCommon,
    votd: esVotd,
    plans: esPlans,
    header: esHeader,
    tools: esTools,
    reader: esReader,
    community: esCommunity,
    games: esGames,
    sermon: esSermon,
    messages: esMessages,
    prayer: esPrayer,
    settings: esSettings,
  },
};

const LanguageContext = createContext();

// Walk a dot-separated path (e.g. "votd.title") through a nested object.
const resolveKey = (obj, path) =>
  path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);

// Replace {token} placeholders with values from vars.
const interpolate = (str, vars) => {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (match, token) =>
    vars[token] !== undefined ? String(vars[token]) : match,
  );
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  // First-run picker: show until the reader explicitly confirms a language.
  const [needsLanguageChoice, setNeedsLanguageChoice] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('appLanguage');
    const onboarded = localStorage.getItem('lumera_lang_onboarded') === '1';
    // Detect the browser language so the picker can pre-select the best option.
    const browserLang = (
      navigator.language ||
      (navigator.languages && navigator.languages[0]) ||
      'en'
    ).toLowerCase();
    if (saved === 'en' || saved === 'es') {
      setLanguage(saved);
    } else if (browserLang.startsWith('es')) {
      setLanguage('es');
    }
    // Show the one-time picker to anyone who has never confirmed a choice.
    if (!onboarded) setNeedsLanguageChoice(true);
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
    localStorage.setItem('appLanguage', newLang);
  };

  // Explicitly set + persist a language (used by the toggle and the picker).
  const setAppLanguage = (lang) => {
    if (lang !== 'en' && lang !== 'es') return;
    setLanguage(lang);
    localStorage.setItem('appLanguage', lang);
  };

  // Called by the first-run picker when the reader confirms their language.
  const confirmLanguageChoice = (lang) => {
    setAppLanguage(lang);
    localStorage.setItem('lumera_lang_onboarded', '1');
    setNeedsLanguageChoice(false);
  };

  // Supports flat keys ('save'), dot-notation ('votd.title') and {token} interpolation.
  const t = (key, vars) => {
    let value = resolveKey(translations[language], key);
    if (value === undefined) value = resolveKey(translations.en, key);
    if (value === undefined) return key;
    return interpolate(value, vars);
  };

  // Intl locale string derived from the active language.
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  return (
    <LanguageContext.Provider value={{ language, locale, toggleLanguage, setAppLanguage, t, needsLanguageChoice, confirmLanguageChoice }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
